/**
 * Startseiten-Header-Animation (Figma: HOME – Header Animation)
 *
 * Großes Logo mit Bogen-Bild in der Hausfarbe. Die gesamte Animation dauert
 * genau 100vh Scrollweg: der Hero (100vh hoch) wird NICHT gepinnt, sondern
 * scrollt ganz normal nach oben, während GSAP ScrollTrigger (scrub) parallel
 *  - das Hintergrundbild zusätzlich nach oben aus dem Bild schiebt und
 *  - das große Logo auf die Größe des Sticky-Logos schrumpft. Das Logo
 *    kompensiert dabei den Scrollweg des Heros, sodass es exakt auf dem
 *    Nav-Logo landet; danach übernimmt das echte Nav-Logo
 *    (<html class="kkg-hero-done">).
 *
 * Funktioniert mit ScrollSmoother (Pin per transform) und auf Mobilgeräten.
 * Bei „prefers-reduced-motion" wird nicht animiert.
 *
 * Markup: global-templates/kkg-hero.php, Styles: src/sass/theme/_kkg_components.scss
 */
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
// lottie-web (UMD-Bundle im Theme): im Rollup-Bundle wird der CommonJS-Zweig
// genommen, daher gibt es kein window.lottie – explizit importieren.
import lottieLib from "./lottie";
import { ScrollSmoother } from "gsap/ScrollSmoother";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

// Anteil des Scrollwegs (0–1), nach dem das Logo im Sticky-Menü angekommen ist.
// Muss deutlich unter 1 liegen: der Hero scrollt schneller nach oben, als das Logo
// schrumpft – nur so bleibt das Logo bis zum Schluss auf dem blauen Hintergrund.
const LOGO_END = 0.7;

// Ab diesem Timeline-Fortschritt übernimmt das Nav-Logo (kleine Toleranz).
const DONE_THRESHOLD = LOGO_END - 0.005;

// Nachlaufzeit (Sekunden) der Animation hinter der Scrollposition.
const SCRUB_SMOOTHING = 0.6;

// Dauer (Sekunden) des Auto-Scrolls zum Inhalt nach dem Lottie-Intro (initStaticHeroIntro).
const INTRO_SCROLL_DURATION = 0.9;

// Pause (Sekunden) zwischen dem sichtbaren Ende des Lottie-Intros und dem Start
// des Auto-Scrolls. Das sichtbare Ende ist der Marker „Hold" der Animation
// (danach steht nur noch das fertige Logo); fehlt der Marker, zählt das
// „complete"-Event.
const INTRO_SCROLL_PAUSE = 0.2;
const INTRO_END_MARKER = "Hold";

// Mindestabstand (ms) zwischen zwei „Zuckern" des Scroll-Signals, wenn der
// Nutzer während des gesperrten Intros zu scrollen versucht.
const INTRO_NUDGE_INTERVAL = 500;

/**
 * @param {object} options
 * @param {object} [options.headroom] Headroom-Instanz – wird während der Animation eingefroren,
 *                                    damit der Header (mit dem Ziel-Logo) sichtbar bleibt.
 */
export function initHeroAnimation(options = {}) {
    const hero = document.querySelector("[data-kkg-hero]");
    if (!hero) {
        return null;
    }

    const html = document.documentElement;
    const heroLogo = hero.querySelector("[data-kkg-hero-logo]");
    const heroSubline = hero.querySelector(".kkg-hero__logo-subline");
    const heroImage = hero.querySelector("[data-kkg-hero-image]");
    const navLogo = document.querySelector("#main-nav .brand-logo img");
    const headroom = options.headroom || null;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!heroLogo || !navLogo) {
        return null;
    }

    // Ab jetzt blendet CSS das Nav-Logo aus, bis die Animation fertig ist.
    html.classList.add("kkg-hero-active");

    if (reduceMotion) {
        html.classList.add("kkg-hero-done");
        return null;
    }

    const setDone = (done) => {
        html.classList.toggle("kkg-hero-done", done);

        if (headroom) {
            if (done) {
                headroom.unfreeze && headroom.unfreeze();
            } else {
                headroom.pin && headroom.pin();
                headroom.freeze && headroom.freeze();
            }
        }
    };

    // Ziel-Transformation: vom Layout-Mittelpunkt des großen Logos (Hero am
    // oberen Viewport-Rand, Scrollposition 0) zum Mittelpunkt des fixierten
    // Nav-Logos. Der Hero scrollt währenddessen (bis LOGO_END) um LOGO_END seiner
    // Höhe nach oben – dieser Weg wird in y hinzugerechnet.
    const measure = () => {
        gsap.set(heroLogo, { clearProps: "transform" });

        const heroRect = hero.getBoundingClientRect();
        const logoRect = heroLogo.getBoundingClientRect();
        const navRect = navLogo.getBoundingClientRect();

        const logoCenterX = logoRect.left + logoRect.width / 2;
        const logoCenterY = (logoRect.top - heroRect.top) + logoRect.height / 2;
        const navCenterX = navRect.left + navRect.width / 2;
        const navCenterY = navRect.top + navRect.height / 2;

        // Das Nav-Logo zeigt nur die Wortmarke: Skalierung über deren Bildhöhe
        // (erstes <img> im Logo-Block; Bogen-Text ist ein <svg>).
        const heroImg = heroLogo.querySelector("img");
        const heroImgH = heroImg ? heroImg.getBoundingClientRect().height : logoRect.height;
        const scale = navRect.height > 0 && heroImgH > 0 ? navRect.height / heroImgH : 0.15;

        return {
            x: navCenterX - logoCenterX,
            y: navCenterY - logoCenterY + heroRect.height * LOGO_END,
            scale: scale,
        };
    };

    let target = measure();

    // Vor jedem Refresh (Resize, Orientation, Bilder geladen) neu messen.
    ScrollTrigger.addEventListener("refreshInit", () => {
        target = measure();
    });

    const timeline = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
            trigger: hero,
            start: "top top",
            end: "bottom top", // Scrolldauer: genau die Hero-Höhe (100vh), kein Pin
            // Geglätteter Scrub: Logo und Bild laufen der Scrollposition weich nach
            // (zusätzlich zum globalen ScrollSmoother in custom-javascript.js).
            scrub: SCRUB_SMOOTHING,
            invalidateOnRefresh: true,
            onRefresh: (self) => setDone(self.progress >= DONE_THRESHOLD),
        },
    });

    // Hintergrundbild: nach oben aus dem Bild schieben (ist vor dem Logo weg).
    if (heroImage) {
        timeline.to(heroImage, { yPercent: -170, duration: LOGO_END * 0.85 }, 0);
    }

    // Unterzeile „APARTMENTS": ausblenden, das Sticky-Logo zeigt nur die Wortmarke.
    if (heroSubline) {
        timeline.to(heroSubline, { opacity: 0, duration: LOGO_END * 0.4 }, 0);
    }

    // Logo: schrumpfen und ins Sticky-Menü fahren – fertig nach LOGO_END des Scrollwegs.
    timeline.to(
        heroLogo,
        {
            x: () => target.x,
            y: () => target.y,
            scale: () => target.scale,
            transformOrigin: "center center",
            duration: LOGO_END,
        },
        0
    );

    // Restlicher Scrollweg ohne Animation (Timeline-Gesamtdauer = 1 = 100vh).
    timeline.to({}, { duration: 1 - LOGO_END }, LOGO_END);

    // Zustand am (geglätteten) Timeline-Fortschritt festmachen: erst wenn das Logo
    // wirklich angekommen ist, übernimmt das Nav-Logo.
    timeline.eventCallback("onUpdate", () => setDone(timeline.progress() >= DONE_THRESHOLD));

    // Initialzustand: Header sichtbar halten, Nav-Logo versteckt.
    setDone(false);

    // Debug-Zugriff (z. B. in der Konsole: kkgHero.timeline.progress(0.5))
    window.kkgHero = { timeline: timeline, measure: measure };

    // Nachdem Logo/Bild geladen sind, Maße aktualisieren.
    const images = hero.querySelectorAll("img");
    images.forEach((img) => {
        if (!img.complete) {
            img.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });
        }
    });
    window.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });

    return timeline;
}

/**
 * Startseiten-Intro: Der statische Header (Lottie-Intro bzw. Bogen-Text + Logo)
 * bleibt nach dem Laden für `delay` Millisekunden stehen, die Seite ist solange gesperrt
 * (kein Scrollen, kein Klicken, keine Tastatur). Danach scrollt die Seite
 * automatisch weich zum ersten Inhalt unter dem Hero, dann wird entsperrt.
 *
 * Das Scroll-Signal ([data-kkg-hero-hint]) zeigt die Sperre an: eine Linie
 * füllt sich mit dem Fortschritt des Intros und wird beim Start des
 * Auto-Scrolls zum Pfeil; Scrollversuche lassen sie kurz zucken. Auf Geräten
 * mit Maus ersetzt außerdem ein Ring mit demselben Fortschritt den Cursor,
 * nach der Animation steht darin der Marken-Pfeil.
 *
 * Übersprungen bei: prefers-reduced-motion, Sprungmarke in der URL, bereits
 * gescrollter Seite (Reload/Zurück-Navigation) oder fehlendem Inhalt.
 *
 * @param {object} options
 * @param {object} [options.headroom] Headroom-Instanz (bleibt während des Intros eingefroren,
 *                                    damit der Header nach dem Scrollen sichtbar bleibt)
 * @param {number} [options.delay]    Wartezeit in ms (Standard 3000)
 */
export function initStaticHeroIntro(options = {}) {
    const hero = document.querySelector(".kkg-hero--static");
    if (!hero) {
        return null;
    }

    const html = document.documentElement;
    const headroom = options.headroom || null;
    const delay = typeof options.delay === "number" ? options.delay : 3000;
    const target = hero.nextElementSibling;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const lottieBox = hero.querySelector("[data-kkg-hero-lottie]");
    const lottie = lottieLib && lottieLib.loadAnimation ? lottieLib : window.lottie;
    const canLottie = !!(lottieBox && lottie && typeof lottie.loadAnimation === "function");
    const hint = hero.querySelector("[data-kkg-hero-hint]");

    const smoother = ScrollSmoother.get();
    const skipIntro = !target || reduceMotion || window.location.hash || window.scrollY > 10
        || (smoother && smoother.scrollTop() > 10);

    if (skipIntro) {
        // Kein Intro (reduzierte Bewegung, Sprungmarke, bereits gescrollt):
        // Lottie trotzdem laden und direkt das Endbild zeigen.
        if (canLottie) {
            const still = lottie.loadAnimation({
                container: lottieBox,
                renderer: "svg",
                loop: false,
                autoplay: false,
                path: lottieBox.getAttribute("data-kkg-hero-lottie"),
                rendererSettings: { preserveAspectRatio: "xMidYMid meet" },
            });
            still.addEventListener("DOMLoaded", () => still.goToAndStop(Math.max(0, still.totalFrames - 1), true));
        }
        return null;
    }

    // Eigener Cursor während der Sperre (nur Geräte mit Maus): ein Ring folgt
    // der Maus und füllt sich wie die Linie; nach der Animation steht darin
    // klein der Marken-Pfeil der Buttons, nach unten gedreht (CSS-Maske,
    // _kkg_components.scss). Der System-Cursor ist solange per CSS
    // ausgeblendet (html.kkg-intro-lock).
    // Das Element hängt direkt an <body>: im ScrollSmoother-Inhalt (transform)
    // wäre position: fixed nicht mehr am Viewport ausgerichtet.
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    let cursor = null;
    const onMouseMove = (event) => {
        cursor.style.transform = "translate3d(" + event.clientX + "px, " + event.clientY + "px, 0)";
        cursor.classList.add("is-visible");
    };
    const onMouseLeave = () => cursor.classList.remove("is-visible");
    const addCursor = () => {
        if (!finePointer || cursor) {
            return;
        }
        cursor = document.createElement("div");
        cursor.className = "kkg-intro-cursor";
        cursor.setAttribute("aria-hidden", "true");
        cursor.innerHTML = '<span class="kkg-intro-cursor__icon">'
            + '<svg class="kkg-intro-cursor__ring" viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" focusable="false">'
            + '<circle class="kkg-intro-cursor__track" cx="20" cy="20" r="18" />'
            + '<circle class="kkg-intro-cursor__fill" cx="20" cy="20" r="18" transform="rotate(-90 20 20)" />'
            + '<circle class="kkg-intro-cursor__dot" cx="20" cy="20" r="2" fill="currentColor" stroke="none" />'
            + "</svg>"
            + '<span class="kkg-intro-cursor__arrow"></span>'
            + "</span>";
        document.body.appendChild(cursor);
        window.addEventListener("mousemove", onMouseMove, { passive: true });
        html.addEventListener("mouseleave", onMouseLeave);
    };
    const removeCursor = () => {
        if (!cursor) {
            return;
        }
        const element = cursor;
        cursor = null;
        window.removeEventListener("mousemove", onMouseMove);
        html.removeEventListener("mouseleave", onMouseLeave);
        element.classList.remove("is-visible");
        window.setTimeout(() => element.remove(), 400); // nach dem Ausblenden (CSS-Transition)
    };

    // Scroll-Signal (Linie + Cursor-Ring): Fortschritt (0–1), Feedback bei
    // Scrollversuchen, Endzustand „Pfeil".
    const signals = () => [hint, cursor].filter(Boolean);
    const setProgress = (progress) => {
        const value = Math.min(1, Math.max(0, progress)).toFixed(3);
        signals().forEach((element) => element.style.setProperty("--kkg-intro-progress", value));
    };
    let lastNudge = 0;
    const nudge = () => {
        const now = Date.now();
        if (now - lastNudge < INTRO_NUDGE_INTERVAL) {
            return;
        }
        lastNudge = now;
        signals().forEach((element) => {
            element.classList.remove("is-nudge");
            void element.offsetWidth; // Reflow, damit die CSS-Animation neu startet
            element.classList.add("is-nudge");
        });
    };

    // Sperren: Smoother pausieren (kein Scrollen), Tastatur-Scrollen und
    // Touch-Gesten abfangen, Pointer-Events per CSS (html.kkg-intro-lock).
    const blockKeys = ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Tab"];
    const onKey = (event) => {
        if (blockKeys.includes(event.key)) {
            event.preventDefault();
            nudge();
        }
    };
    // Wheel/Touch in der Capture-Phase abfangen und nicht weiterreichen: Bei
    // body { pointer-events: none } ist <html> das Event-Ziel, und der
    // Input-Observer des pausierten ScrollSmoothers (_nestedScroll) liefe von
    // dort bis zu document hoch -> getComputedStyle(document) wirft TypeError.
    const onTouch = (event) => {
        event.preventDefault();
        event.stopPropagation();
        nudge();
    };
    const onWheel = (event) => {
        event.preventDefault();
        event.stopPropagation();
        nudge();
    };
    const captureOpts = { passive: false, capture: true };

    const lock = () => {
        html.classList.add("kkg-intro-lock");
        addCursor();
        if (smoother) {
            smoother.paused(true);
        }
        if (headroom) {
            headroom.pin && headroom.pin();
            headroom.freeze && headroom.freeze();
        }
        window.addEventListener("keydown", onKey, { passive: false });
        window.addEventListener("touchmove", onTouch, captureOpts);
        window.addEventListener("wheel", onWheel, captureOpts);
    };

    const unlock = () => {
        html.classList.remove("kkg-intro-lock");
        removeCursor();
        window.removeEventListener("keydown", onKey);
        window.removeEventListener("touchmove", onTouch, captureOpts);
        window.removeEventListener("wheel", onWheel, captureOpts);
        if (headroom) {
            headroom.unfreeze && headroom.unfreeze();
            // Zustand nach dem programmatischen Scrollen nachziehen (Headroom
            // 0.12: update() braucht Scroll-Details, daher direkt notTop/pin):
            // .headroom--not-top -> Nav-Logo sichtbar, Header bleibt eingeblendet.
            if (window.scrollY > 10) {
                headroom.notTop && headroom.notTop();
            }
            headroom.pin && headroom.pin();
        }
    };

    // Zielposition: erster Inhalt bündig unter dem fixierten Header
    const headerHeight = () => {
        const nav = document.getElementById("wrapper-navbar");
        return nav ? nav.getBoundingClientRect().height : 0;
    };

    const scrollDown = () => {
        if (smoother) {
            smoother.paused(false);
            const y = Math.max(0, smoother.offset(target, "top top") - headerHeight());
            // Eigener Tween statt smoother.scrollTo(target, true): kürzer und mit
            // sauberem Ease-in-out. Pro Tick scrollTo(wert, false) – der Inhalt
            // folgt exakt, ohne den 1,5-s-Nachlauf des Smoothers (der würde nach
            // dem Entsperren weiterlaufen und Headroom den Header ausblenden lassen).
            const proxy = { y: smoother.scrollTop() };
            gsap.to(proxy, {
                y: y,
                duration: INTRO_SCROLL_DURATION,
                ease: "power3.inOut",
                overwrite: true,
                onUpdate: () => smoother.scrollTo(proxy.y, false),
                onComplete: () => {
                    smoother.scrollTo(y, false);
                    unlock();
                },
            });
        } else {
            const y = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerHeight());
            window.scrollTo({ top: y, behavior: "smooth" });
            gsap.delayedCall(INTRO_SCROLL_DURATION, unlock);
        }
    };

    lock();

    // Lottie-Intro (Claude Design „Neumeier Logo Intro"): Container mit
    // data-kkg-hero-lottie statt Bogen-Text + Logo. Der Auto-Scroll startet
    // INTRO_SCROLL_PAUSE nach dem sichtbaren Ende der Animation (Marker
    // INTRO_END_MARKER, sonst „complete"), frühestens aber nach `delay`.
    // Ohne Lottie (oder wenn die Datei nicht lädt) gilt nur die Verzögerung.
    let started = false;
    const start = () => {
        if (!started) {
            started = true;
            // Scroll-Signal: Linie und Ring voll, Pfeil erscheint
            setProgress(1);
            signals().forEach((element) => element.classList.add("is-done"));
            scrollDown();
        }
    };

    const delayDone = new Promise((resolve) => gsap.delayedCall(delay / 1000, resolve));
    let animationDone = Promise.resolve();

    if (canLottie) {
        animationDone = new Promise((resolve) => {
            const anim = lottie.loadAnimation({
                container: lottieBox,
                renderer: "svg",
                loop: false,
                autoplay: true,
                path: lottieBox.getAttribute("data-kkg-hero-lottie"),
                rendererSettings: { preserveAspectRatio: "xMidYMid meet" },
            });
            // Sichtbares Ende: Beginn des Markers „Hold" (der Rest ist Standbild).
            let endFrame = Infinity;
            // Bezugsgröße für das Scroll-Signal (ohne Marker: gesamte Animation)
            let progressEnd = 0;
            anim.addEventListener("DOMLoaded", () => {
                const marker = (anim.markers || []).find((m) => m && m.payload && m.payload.name === INTRO_END_MARKER);
                if (marker) {
                    endFrame = marker.time;
                }
                progressEnd = marker ? marker.time : anim.totalFrames;
            });
            const finish = () => gsap.delayedCall(INTRO_SCROLL_PAUSE, resolve);
            anim.addEventListener("enterFrame", () => {
                if (progressEnd > 0 && !started) {
                    setProgress(anim.currentFrame / progressEnd);
                }
                if (anim.currentFrame >= endFrame) {
                    endFrame = Infinity;
                    finish();
                }
            });
            anim.addEventListener("complete", finish);
            anim.addEventListener("data_failed", resolve);
            anim.addEventListener("error", resolve);
            // Sicherheitsnetz: sehr lange Animationen nicht endlos sperren
            gsap.delayedCall(12, resolve);
            window.kkgHeroIntroAnimation = anim;
        });
    } else {
        // Ohne Lottie läuft die Linie über die Wartezeit
        const proxy = { progress: 0 };
        gsap.to(proxy, {
            progress: 1,
            duration: delay / 1000,
            ease: "none",
            onUpdate: () => setProgress(proxy.progress),
        });
    }

    Promise.all([delayDone, animationDone]).then(start);

    // Sicherheitsnetz: Tab-Wechsel o. ä. – nie dauerhaft gesperrt lassen
    const failsafe = gsap.delayedCall(delay / 1000 + 16, unlock);

    return { unlock: unlock, start: start, failsafe: failsafe };
}
